import { isBoxLayout, isPinComponentLocked, layoutHasLockedAspectRatio, useUpdatePin } from '@lessonup/pin-renderer';
import {
  Box,
  HotspotPinComponent,
  isHotspotPinComponent,
  isLinePinComponent,
  LinePinComponent,
  PinComponent,
} from '@lessonup/pins-shared';
import { BodyText, SpaceBetween, spacing, styled } from '@lessonup/ui-components';
import React from 'react';
import { useFeatureFlags } from '../../../../../../../utils/featureFlags/useFeatureFlags';
import { useEditorTranslation } from '../../../../../hooks/useEditorTranslation';
import {
  setKeepRatioAction,
  setSizeAction,
} from '../../../../../utils/pinComponentSettings/sharedPinComponentSettings';
import { EditorTextField } from '../../EditorInputFields/EditorTextField';
import { ListItemSwitchSetting } from '../../ListItemSwitchSetting/ListItemSwitchSetting';

const SIZE_INCREMENT_STEP = 5;

export interface LayoutSizeSettingProps {
  pinComponent: PinComponent;
}

export const LayoutSizeSetting: React.FC<LayoutSizeSettingProps> = ({ pinComponent }) => {
  const dispatch = useUpdatePin();
  const { t } = useEditorTranslation();
  const { isFeatureEnabled } = useFeatureFlags();
  if (!isFeatureEnabled('editorSidebarLayoutSize')) return null;
  if (!isBoxLayout(pinComponent.layout) || isLinePinComponent(pinComponent)) return null;

  return (
    <SpaceBetween direction="y" spacing={spacing.size8}>
      {isHotspotPinComponent(pinComponent) ? (
        <PinComponentSizeSingleInput pinComponent={pinComponent} layout={pinComponent.layout} />
      ) : (
        <PinComponentSizeDoubleInputs pinComponent={pinComponent} layout={pinComponent.layout} />
      )}
      {!isHotspotPinComponent(pinComponent) && (
        <ListItemSwitchSetting
          key={`${pinComponent.id}-keep-ratio-setting`}
          id={`${pinComponent.id}-keep-ratio-setting`}
          ariaLabel={t('contextSidebar.settings.layout.keepRatio.description')}
          text={t('contextSidebar.settings.layout.keepRatio.title')}
          secondLine={t('contextSidebar.settings.layout.keepRatio.description')}
          checked={layoutHasLockedAspectRatio(pinComponent.layout)}
          onChange={(event) => {
            const action = setKeepRatioAction(pinComponent, event);
            if (action) {
              dispatch(action);
            }
          }}
        />
      )}
    </SpaceBetween>
  );
};

interface PinComponentSizeDoubleInputsProps {
  pinComponent: Exclude<PinComponent, LinePinComponent | HotspotPinComponent>;
  layout: Box;
}

const PinComponentSizeDoubleInputs: React.FC<PinComponentSizeDoubleInputsProps> = ({ pinComponent, layout }) => {
  const dispatch = useUpdatePin();
  const { t } = useEditorTranslation();
  const isLocked = isPinComponentLocked(pinComponent);

  return (
    <SpaceBetween direction="x" spacing={spacing.size4}>
      <StyledBodyText>{t('contextSidebar.settings.layout.width')}</StyledBodyText>
      <StyledTextField
        id="w"
        type="number"
        value={layout.size.width}
        min={0}
        max={1920}
        step={SIZE_INCREMENT_STEP}
        onChange={(event) => {
          const action = setSizeAction(pinComponent, 'width', event);
          if (action) {
            dispatch(action);
          }
        }}
        disabled={isLocked}
      />
      <StyledBodyText>{t('contextSidebar.settings.layout.height')}</StyledBodyText>
      <StyledTextField
        id="h"
        type="number"
        value={layout.size.height}
        min={0}
        max={1920}
        step={SIZE_INCREMENT_STEP}
        onChange={(event) => {
          const action = setSizeAction(pinComponent, 'height', event);
          if (action) {
            dispatch(action);
          }
        }}
        disabled={isLocked}
      />
    </SpaceBetween>
  );
};

interface PinComponentSizeSingleInputProps {
  pinComponent: HotspotPinComponent;
  layout: Box;
}

const PinComponentSizeSingleInput: React.FC<PinComponentSizeSingleInputProps> = ({ pinComponent, layout }) => {
  const dispatch = useUpdatePin();
  const isLocked = isPinComponentLocked(pinComponent);

  return (
    <SpaceBetween direction="x" spacing={spacing.size4}>
      <StyledTextField
        id="w"
        type="number"
        value={layout.size.width}
        min={0}
        max={1920}
        step={SIZE_INCREMENT_STEP}
        onChange={(event) => {
          const action = setSizeAction(pinComponent, 'both', event);
          if (action) {
            dispatch(action);
          }
        }}
        disabled={isLocked}
      />
    </SpaceBetween>
  );
};

const StyledBodyText = styled(BodyText)`
  width: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledTextField = styled(EditorTextField)`
  flex-grow: 1;
`;
